import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify, useRefresh, usePermissions } from 'react-admin';
import {
  Box, Card, CardContent, Typography, Stack, Button, TextField, Alert, CircularProgress, IconButton, Autocomplete,
  Table, TableHead, TableRow, TableCell, TableBody, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import {
  card, btn, tbl, typo, inputSx, badgeStyle, NAVY,
  WEBSITE_APPROVAL_STATUS, WEBSITE_SHIPPING_REVIEW_STATE, WEBSITE_PAYMENT_REQUEST_STATUS,
} from '../styles/ds';
import { websiteRequest, fmtUsd } from '../website/websiteApi';

/*
 * Website order panel (Phase E part 2; CRM Admin only — WD-012). Shows the
 * order as the customer submitted it and every later revision, and lets the
 * admin:
 *  - update amounts (lines, prices, quantities, discount, shipping) — each a
 *    new revision with a reason; an approved order then needs approval again;
 *  - approve the final amount, which also requests payment — the customer
 *    sees the amount and the Zelle instructions under "My orders";
 *  - reject or cancel, with a reason (the coupon use is given back if unpaid).
 * Payments are recorded in the payments section below, as for any order.
 */

const toInput = minor => (minor === null || minor === undefined ? '' : String(minor / 100));
const toMinor = s => (/^\d+(\.\d{1,2})?$/.test(String(s).trim()) ? Math.round(Number(s) * 100) : NaN);
const fullName = c => [c?.firstName, c?.lastName].filter(Boolean).join(' ');
const ACTOR = { customer: 'הלקוח', staff: 'צוות' };
// Outbox (Phase F) — mirrors services/websiteEmailTemplates.js and the website_outbox statuses.
const EMAIL_TEMPLATE = {
  order_received: 'אישור קבלת הזמנה ללקוח', admin_new_order: 'התראה לעסק: הזמנה חדשה',
  payment_requested: 'בקשת תשלום ללקוח', payment_received: 'אישור תשלום ללקוח', order_shipped: 'הודעת משלוח ללקוח',
  order_ready_for_pickup: 'הודעה ללקוח: מוכן לאיסוף',
  order_cancelled: 'הודעת ביטול ללקוח', admin_order_cancelled: 'התראה לעסק: הלקוח ביטל',
};
const EMAIL_STATUS = {
  pending: 'ממתין לשליחה', sent: 'נשלח', logged: 'לא נשלח — המייל לא מוגדר', superseded: 'לא נשלח — הוחלף בגרסה חדשה', dead: 'נכשל',
};

const Totals = ({ t }) => {
  const row = (label, v, strong) => (
    <Stack direction="row" justifyContent="space-between" key={label}>
      <Typography fontSize={14} fontWeight={strong ? 700 : 400}>{label}</Typography>
      <Typography fontSize={14} fontWeight={strong ? 700 : 400} dir="ltr">{v}</Typography>
    </Stack>
  );
  const pending = 'ממתין לקביעת משלוח';
  return (
    <Box sx={{ maxWidth: 360, ml: 'auto' }}>
      {row('מוצרים', fmtUsd(t.merchandiseSubtotalMinor))}
      {t.merchandiseDiscountMinor > 0 && row('הנחה על המוצרים', `−${fmtUsd(t.merchandiseDiscountMinor)}`)}
      {row('משלוח', t.shippingBeforeDiscountMinor === null ? pending : fmtUsd(t.shippingBeforeDiscountMinor))}
      {t.shippingDiscountMinor > 0 && row('הנחה על המשלוח', `−${fmtUsd(t.shippingDiscountMinor)}`)}
      {row('סה"כ', t.totalMinor === null ? pending : fmtUsd(t.totalMinor), true)}
    </Box>
  );
};

const LinesTable = ({ lines }) => (
  <Box className="crm-table-scroll" sx={tbl.container}>
    <Table size="small">
      <TableHead><TableRow><TableCell>מוצר</TableCell><TableCell align="center">כמות</TableCell><TableCell align="center">מחיר ליחידה</TableCell><TableCell align="center">סה"כ</TableCell></TableRow></TableHead>
      <TableBody>
        {lines.map(l => (
          <TableRow key={l.lineId}>
            <TableCell>
              <Typography fontSize={14} fontWeight={600} dir="ltr" textAlign="right">{l.productName}</Typography>
              <Typography fontSize={12} color="text.secondary" dir="ltr" textAlign="right">{l.selections.map(s => `${s.label}: ${s.value}`).join(' · ')}</Typography>
            </TableCell>
            <TableCell align="center">{l.quantity}</TableCell>
            <TableCell align="center" dir="ltr">{fmtUsd(l.unitPrice.amountMinor)}</TableCell>
            <TableCell align="center" dir="ltr">{fmtUsd(l.lineTotal.amountMinor)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

/** Admin revision: edit lines/prices/discounts/shipping; a reason is required. */
const ReviseDialog = ({ order, onClose, onDone }) => {
  const notify = useNotify();
  const cur = order.revisions[0];
  const [lines, setLines] = useState(cur.lines.map((l, i) => ({
    lineId: l.lineId, configurationId: cur.configurations[i].configurationId, name: l.productName,
    detail: l.selections.map(s => `${s.label}: ${s.value}`).join(' · '), quantity: String(l.quantity), price: toInput(l.unitPrice.amountMinor),
  })));
  const [discount, setDiscount] = useState(toInput(cur.totals.merchandiseDiscountMinor));
  const [shipping, setShipping] = useState(toInput(cur.totals.shippingBeforeDiscountMinor));
  const [shipDiscount, setShipDiscount] = useState(toInput(cur.totals.shippingDiscountMinor ?? 0));
  const [reason, setReason] = useState('');
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      websiteRequest(`/website_orders/configurations?q=${encodeURIComponent(search)}`).then(setOptions).catch(() => setOptions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const set = (i, k, v) => setLines(ls => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const subtotal = lines.reduce((s, l) => s + (toMinor(l.price) || 0) * (Number(l.quantity) || 0), 0);
  const ship = shipping.trim() === '' ? null : toMinor(shipping);
  const total = ship === null ? null : subtotal - (toMinor(discount) || 0) + ship - (toMinor(shipDiscount) || 0);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        version: order.version, reason,
        lines: lines.map(l => ({ lineId: l.lineId, configurationId: l.configurationId, quantity: Number(l.quantity), unitPriceMinor: toMinor(l.price) })),
        merchandiseDiscountMinor: discount.trim() === '' ? 0 : toMinor(discount),
        shippingMinor: ship,
        shippingDiscountMinor: shipDiscount.trim() === '' ? 0 : toMinor(shipDiscount),
      };
      const r = await websiteRequest(`/website_orders/${order.orderId}/revisions`, 'POST', body);
      notify(r.couponApplies === false ? 'נשמר. שים לב: הקופון כבר לא עומד בתנאי הסכום — בדוק את ההנחה.' : 'הסכומים עודכנו (גרסה חדשה)', { type: r.couponApplies === false ? 'warning' : 'success' });
      onDone(r.order);
    } catch (err) { notify(err.message, { type: 'error' }); }
    finally { setSaving(false); }
  };

  const money = (value, onChange, label) => (
    <TextField size="small" label={label} value={value} onChange={e => onChange(e.target.value)} sx={{ ...inputSx, width: 170 }}
      inputProps={{ dir: 'ltr', inputMode: 'decimal' }} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
  );

  return (
    <Dialog open onClose={onClose} dir="rtl" maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>עדכון סכומים — גרסה חדשה</DialogTitle>
      <DialogContent dividers>
        <Box className="crm-table-scroll" sx={tbl.container}>
          <Table size="small">
            <TableHead><TableRow><TableCell>מוצר</TableCell><TableCell>כמות</TableCell><TableCell>מחיר ליחידה</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>
              {lines.map((l, i) => (
                <TableRow key={l.lineId}>
                  <TableCell>
                    <Typography fontSize={14} fontWeight={600} dir="ltr" textAlign="right">{l.name}</Typography>
                    <Typography fontSize={12} color="text.secondary" dir="ltr" textAlign="right">{l.detail}</Typography>
                  </TableCell>
                  <TableCell><TextField size="small" value={l.quantity} onChange={e => set(i, 'quantity', e.target.value)} sx={{ ...inputSx, width: 80 }} inputProps={{ dir: 'ltr', inputMode: 'numeric', 'aria-label': `כמות ${l.name}` }} /></TableCell>
                  <TableCell>
                    <TextField size="small" value={l.price} onChange={e => set(i, 'price', e.target.value)} sx={{ ...inputSx, width: 130 }}
                      inputProps={{ dir: 'ltr', inputMode: 'decimal', 'aria-label': `מחיר ${l.name}` }} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                  </TableCell>
                  <TableCell padding="checkbox">
                    <IconButton size="small" aria-label={`הסרת ${l.name}`} disabled={lines.length === 1} onClick={() => setLines(ls => ls.filter((_, j) => j !== i))}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Autocomplete sx={{ mt: 2, maxWidth: 480 }} size="small" options={options} filterOptions={x => x}
          getOptionLabel={o => `${o.name} · ${Object.values(o.selections).join(' / ')}`}
          onInputChange={(_, v) => setSearch(v)} value={null}
          onChange={(_, o) => {
            if (!o) return;
            setLines(ls => [...ls, {
              lineId: crypto.randomUUID?.() ?? String(Date.now()), configurationId: o.id, name: o.name,
              detail: Object.entries(o.selections).map(([k, v]) => `${k}: ${v}`).join(' · '), quantity: '1', price: toInput(o.priceMinor),
            }]);
          }}
          renderInput={params => <TextField {...params} label="הוספת מוצר" placeholder="חיפוש לפי שם מוצר באתר" InputProps={{ ...params.InputProps, startAdornment: <AddIcon fontSize="small" /> }} />} />

        <Stack direction="row" spacing={2} mt={3} flexWrap="wrap" useFlexGap>
          {money(discount, setDiscount, 'הנחה על המוצרים')}
          {money(shipping, setShipping, 'מחיר משלוח')}
          {money(shipDiscount, setShipDiscount, 'הנחה על המשלוח')}
        </Stack>
        <Typography sx={{ ...typo.meta, mt: 1 }}>מחיר משלוח ריק — המשלוח עדיין לא נקבע (אי אפשר לאשר עד שיוזן).</Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          סה"כ מוצרים {fmtUsd(subtotal)} · סה"כ להזמנה {total === null || Number.isNaN(total) ? 'ממתין למחיר משלוח' : fmtUsd(total)}
        </Alert>
        <TextField label="סיבת השינוי (נשמרת בהיסטוריה)" value={reason} onChange={e => setReason(e.target.value)} fullWidth multiline minRows={2}
          sx={{ ...inputSx, mt: 2 }} inputProps={{ maxLength: 500 }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} sx={btn.cancel}>ביטול</Button>
        <Button variant="contained" onClick={save} disabled={saving || !reason.trim()} sx={btn.primary}>שמירת גרסה</Button>
      </DialogActions>
    </Dialog>
  );
};

const ReasonDialog = ({ title, text, confirm, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onClose={onClose} dir="rtl" maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography mb={2}>{text}</Typography>
        <TextField label="סיבה" value={reason} onChange={e => setReason(e.target.value)} multiline minRows={2} fullWidth autoFocus
          sx={inputSx} inputProps={{ maxLength: 500 }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} sx={btn.cancel}>חזרה</Button>
        <Button variant="contained" color="error" disabled={busy || !reason.trim()}
          onClick={async () => { setBusy(true); try { await onConfirm(reason); } finally { setBusy(false); } }}>{confirm}</Button>
      </DialogActions>
    </Dialog>
  );
};

const WebsiteOrderPanel = ({ orderId, orderStatus }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();
  const [order, setOrder] = useState(null);
  const [dialog, setDialog] = useState(null);       // 'revise' | 'reject' | 'cancel'
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    websiteRequest(`/website_orders/${orderId}`).then(setOrder).catch(err => notify(err.message, { type: 'error' }));
  }, [orderId, notify]);
  useEffect(() => { if (permissions === 'admin') load(); }, [load, permissions]);

  const cur = order?.revisions[0];
  const closed = orderStatus === 'cancelled' || orderStatus === 'shipped' || orderStatus === 'delivered';
  const canApprove = useMemo(() => order && !closed && ['pending', 'reapproval_required'].includes(order.approvalStatus) && cur?.totals.totalMinor !== null,
    [order, closed, cur]);

  if (permissions !== 'admin') return null;
  if (!order) return <Card sx={card.section}><CardContent><Box p={3} textAlign="center"><CircularProgress size={28} /></Box></CardContent></Card>;

  const done = updated => { setDialog(null); setOrder(updated); refresh(); };
  const act = async (action, body = {}) => {
    setBusy(true);
    try {
      const r = await websiteRequest(`/website_orders/${orderId}/${action}`, 'POST', { version: order.version, ...body });
      notify({ approve: 'הסכום אושר ונשלחה בקשת תשלום', reject: 'ההזמנה נדחתה', cancel: 'ההזמנה בוטלה' }[action], { type: 'success' });
      done(r.order);
    } catch (err) { notify(err.message, { type: 'error' }); }
    finally { setBusy(false); }
  };

  return (
    <Card sx={card.section}>
      <CardContent className="crm-card-content" sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
          <Typography sx={typo.sectionTitle}>הזמנה מהאתר</Typography>
          <span style={badgeStyle(WEBSITE_APPROVAL_STATUS, order.approvalStatus)}>{WEBSITE_APPROVAL_STATUS[order.approvalStatus]?.label}</span>
          <span style={badgeStyle(WEBSITE_SHIPPING_REVIEW_STATE, order.shippingReviewState)}>{WEBSITE_SHIPPING_REVIEW_STATE[order.shippingReviewState]?.label}</span>
          <span style={badgeStyle(WEBSITE_PAYMENT_REQUEST_STATUS, order.paymentRequestStatus)}>{WEBSITE_PAYMENT_REQUEST_STATUS[order.paymentRequestStatus]?.label}</span>
        </Stack>

        {order.overThreshold && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            סכום המוצרים מעל תקרת ההזמנה ({fmtUsd(order.reviewThresholdMinor)} בזמן ההזמנה) — מומלץ לבדוק לפני אישור.
          </Alert>
        )}
        {order.shippingReviewState === 'required' && !closed && (
          <Alert severity="warning" sx={{ mb: 2 }}>מחיר המשלוח לא חושב אוטומטית (מעל הטווחים). יש לקבוע אותו ב"עדכון סכומים" לפני אישור.</Alert>
        )}
        {order.closeReason && <Alert severity="info" sx={{ mb: 2 }}>סיבת {order.approvalStatus === 'rejected' ? 'הדחייה' : 'הביטול'}: {order.closeReason}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
          <Box>
            <Typography fontWeight={600} fontSize={14}>פרטי קשר</Typography>
            <Typography fontSize={14} dir="ltr" textAlign="right">{fullName(cur.contact)} · {cur.contact.phone}</Typography>
            <Typography fontSize={14} dir="ltr" textAlign="right">{order.accountEmail}</Typography>
          </Box>
          <Box>
            <Typography fontWeight={600} fontSize={14}>כתובת {cur.shipping.method === 'pickup' ? '(לחשבונית — איסוף עצמי)' : 'למשלוח'}</Typography>
            {cur.address && (
              <Typography fontSize={14} dir="ltr" textAlign="right">
                {[cur.address.recipientName, cur.address.line1, cur.address.line2, cur.address.city, cur.address.region, cur.address.postalCode, cur.address.countryCode].filter(Boolean).join(', ')}
              </Typography>
            )}
          </Box>
        </Box>
        {cur.notes && <Alert severity="info" icon={false} sx={{ mb: 2 }}><b>הערות הלקוח:</b> <span dir="auto">{cur.notes}</span></Alert>}

        <LinesTable lines={cur.lines} />
        <Box mt={2}><Totals t={cur.totals} /></Box>
        {order.coupon && (
          <Typography sx={{ ...typo.meta, mt: 1 }}>
            קופון: <b dir="ltr">{order.coupon.code}</b>{cur.coupon?.applies === false ? ' — כבר לא עומד בתנאי הסכום' : ''}{order.coupon.released ? ' (השימוש שוחרר)' : ''}
          </Typography>
        )}

        {!closed && (
          <Stack direction="row" spacing={1.5} mt={3} flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={() => act('approve')} disabled={busy || !canApprove} sx={btn.primary}>אישור הסכום ובקשת תשלום</Button>
            <Button variant="outlined" onClick={() => setDialog('revise')} disabled={busy}>עדכון סכומים</Button>
            {order.approvalStatus !== 'rejected' && <Button variant="outlined" color="error" onClick={() => setDialog('reject')} disabled={busy}>דחיית ההזמנה</Button>}
            <Button variant="outlined" color="error" onClick={() => setDialog('cancel')} disabled={busy}>ביטול</Button>
          </Stack>
        )}
        {order.approvalStatus === 'approved' && !closed && (
          <Typography sx={{ ...typo.meta, mt: 1.5 }}>
            הלקוח רואה את הסכום הסופי ואת הוראות ה-Zelle ב"ההזמנות שלי" באתר. את התשלום, כשיתקבל, רושמים בחלק התשלומים למטה.
          </Typography>
        )}

        <Typography fontWeight={600} fontSize={14} mt={3} mb={1}>מיילים</Typography>
        {order.emails?.length ? (
          <Stack spacing={0.5}>
            {order.emails.map((e, i) => (
              <Typography key={i} fontSize={13} color={e.status === 'dead' ? 'error.main' : 'text.secondary'}>
                {EMAIL_TEMPLATE[e.template] ?? e.template} · <span dir="ltr">{e.recipient}</span> · {EMAIL_STATUS[e.status] ?? e.status}
                {' · '}{new Date(e.finished_at || e.created_at).toLocaleString('he-IL')}
                {e.status === 'dead' && e.last_error ? ` · ${e.last_error}` : ''}
              </Typography>
            ))}
          </Stack>
        ) : <Typography sx={typo.meta}>עדיין לא נוצרו מיילים להזמנה הזו.</Typography>}

        <Typography fontWeight={600} fontSize={14} mt={3} mb={1}>היסטוריית גרסאות</Typography>
        <Stack spacing={0.5}>
          {order.revisions.map(r => (
            <Typography key={r.id} fontSize={13} color="text.secondary">
              גרסה {r.number} · {new Date(r.createdAt).toLocaleString('he-IL')} · {r.createdByName || ACTOR[r.createdByType]}
              {r.reason ? ` · ${r.reason}` : ''} · סה"כ {r.totals.totalMinor === null ? 'ממתין למשלוח' : fmtUsd(r.totals.totalMinor)}
              {r.id === order.approvedRevisionId ? ' · ✓ מאושרת' : ''}
            </Typography>
          ))}
        </Stack>
      </CardContent>

      {dialog === 'revise' && <ReviseDialog order={order} onClose={() => setDialog(null)} onDone={done} />}
      {dialog === 'reject' && (
        <ReasonDialog title="דחיית הזמנה" confirm="דחייה" onClose={() => setDialog(null)} onConfirm={reason => act('reject', { reason })}
          text="ההזמנה תסומן כנדחתה ותבוטל. הלקוח יראה זאת ב&quot;ההזמנות שלי&quot;. אם התקבל תשלום, ההחזר נעשה ידנית." />
      )}
      {dialog === 'cancel' && (
        <ReasonDialog title="ביטול הזמנה" confirm="ביטול ההזמנה" onClose={() => setDialog(null)} onConfirm={reason => act('cancel', { reason })}
          text="ההזמנה תבוטל. אם התקבל תשלום, ההחזר נעשה ידנית." />
      )}
    </Card>
  );
};

export default WebsiteOrderPanel;
